import cn from 'clsx';
import * as React from 'react';
import { forwardRef } from 'react';

import { IntentVals } from '../../enhancers';
import { splitBoxProps } from '../../utils';
import { Box } from '../Box';
import { BoxOwnProps, PolymorphicComponentProps } from '../Box/types';
import { Flex } from '../Flex';
import { Icon, IIconProps, isIconProp } from '../Icon';
import { Tooltip } from '../Tooltip';
import { AppearanceVals, sizes, variants } from './variants';

export type BadgeOwnProps = {
  /** Name of a Font Awesome Icon to render with badge*/
  icon?: IIconProps['icon'] | React.ReactElement;

  iconRight?: IIconProps['icon'] | React.ReactElement;

  /**
   * Whether this badge should use large styles.
   *
   * @default false
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Badge appearance style.
   *
   * @default default
   */
  appearance?: AppearanceVals;

  /**
   * Intent style for the badge. Setting a color scheme will override this.
   * @default false
   */
  intent?: IntentVals;

  /**
   * Appends a remove icon to the right of badge and executes this callback function on click
   * @default false
   */
  onRemove?: (e: any) => void;
};

const defaultElement = 'span';

export type BadgeProps<E extends React.ElementType = typeof defaultElement> = PolymorphicComponentProps<
  E,
  BadgeOwnProps
>;

export const Badge = React.memo(
  forwardRef<HTMLElement, BadgeProps>(function Badge(
    {
      appearance = 'solid',
      size = 'md',
      as = defaultElement,
      className,
      intent,
      icon,
      iconRight,
      onRemove,
      children,
      ...props
    },
    ref,
  ) {
    const { remainingProps } = splitBoxProps(props);

    const stateProps: Partial<BoxOwnProps> = {
      ...variants[appearance].default,
      ...variants[appearance][intent],
    };

    const [overflowActive, setOverflowActive] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLSpanElement>(null);
    const isOverflow = ({ current }: React.RefObject<HTMLDivElement>) => {
      return (current?.offsetWidth || 0) < (contentRef?.current?.offsetWidth || 0);
    };
    React.useEffect(() => {
      setOverflowActive(isOverflow(containerRef));
    }, [containerRef]);

    const badge = (
      <Box
        ref={ref}
        as={as}
        h={sizes[size].h}
        px={sizes[size].px}
        py={sizes[size].py}
        fontSize={sizes[size].fontSize}
        rounded={sizes[size].rounded}
        borderColor="on-primary"
        whitespace="nowrap"
        className={cn('sl-badge', className, { 'sl-badge--remove': !!onRemove })}
        maxW="full"
        {...stateProps}
        {...remainingProps}
      >
        <Flex justifyItems="start" alignItems="center" w="full">
          {icon ? <BadgeIcon icon={icon} size={size} hasContent={!!children} /> : null}

          <Flex flexShrink overflowX="hidden">
            <Box textOverflow="truncate" whitespace="nowrap" overflowX="hidden" w="auto" ref={containerRef}>
              <span ref={contentRef}>{children}</span>
            </Box>
          </Flex>

          {iconRight && !onRemove ? <BadgeRightIcon icon={iconRight} size={size} /> : null}
          {onRemove ? (
            <Box as={'a'} onClick={onRemove} ml={0.5}>
              <BadgeRightIcon icon={['fas', 'times-circle']} size={size} />
            </Box>
          ) : null}
        </Flex>
      </Box>
    );
    return overflowActive ? <Tooltip renderTrigger={badge}>{children}</Tooltip> : badge;
  }),
);

const BadgeIcon = ({
  icon,
  size,
  hasContent,
  pulse,
}: Pick<BadgeOwnProps, 'icon' | 'size'> & {
  pulse?: boolean;
  hasContent: Boolean;
}) => {
  let elem = icon;
  if (isIconProp(icon)) {
    elem = <Icon icon={icon} style={{ fontSize: sizes[size].iconSize }} pulse={pulse} fixedWidth />;
  }

  return (
    <Box
      data-testid="icon"
      mr={hasContent ? sizes[size].leftIconMr : undefined}
      mx={hasContent ? undefined : sizes[size].leftIconMx}
    >
      {elem}
    </Box>
  );
};

const BadgeRightIcon = ({ icon, size }: Pick<BadgeOwnProps, 'icon' | 'size'>) => {
  let elem = icon;
  if (isIconProp(icon)) {
    elem = (
      <Icon
        icon={icon}
        style={{
          fontSize: sizes[size].iconSize,
        }}
        fixedWidth
      />
    );
  }

  return (
    <Box data-testid="iconright" ml={sizes[size].rightIconMl} mr={sizes[size].rightIconMr}>
      {elem}
    </Box>
  );
};
